'use client';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import {
	type ComponentPropsWithoutRef,
	type ReactNode,
	useState,
} from 'react';
import { cn } from '../../utils/cn';
import { AccordionContent, AccordionTrigger } from './accordion.components';
import {
	type AccordionVariant,
	accordionItemVariants,
	accordionVariants,
} from './variants';

type AccordionRootProps = ComponentPropsWithoutRef<
	typeof AccordionPrimitive.Root
>;

interface AccordionProps
	extends Omit<AccordionRootProps, 'type' | 'value' | 'onValueChange'> {
	items: {
		id: string | number;
		label: ReactNode;
		content: ReactNode;
	}[];
	variant?: AccordionVariant;
	itemProps?: Partial<ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>>;
	defaultValue?: string;
}

function AccordionContainer({
	items,
	variant = 'outline',
	className,
	itemProps,
	defaultValue,
	...props
}: AccordionProps) {
	const [value, setValue] = useState<string | undefined>(defaultValue);

	return (
		<AccordionPrimitive.Root
			data-slot="accordion"
			type="single"
			collapsible
			value={value}
			onValueChange={setValue}
			className={cn(accordionVariants({ variant }), className)}
			{...props}
		>
			{items.map((item) => (
				<AccordionPrimitive.Item
					key={item.id}
					value={String(item.id)}
					className={cn(
						accordionItemVariants({ variant }),
						variant === 'separated' &&
							value === String(item.id) &&
							'bg-transparent',
					)}
					{...itemProps}
				>
					<AccordionTrigger>{item.label}</AccordionTrigger>
					<AccordionContent>{item.content}</AccordionContent>
				</AccordionPrimitive.Item>
			))}
		</AccordionPrimitive.Root>
	);
}

export { AccordionContainer };
